import { Tooltip, Tag, Empty, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OlitaskHeaderIcon from "components/olitask/OlitaskHeaderIcon";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";

const accountLabel = (account) => account?.name || "Workspace";

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const isOrganizationAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "ORGANIZATION" ||
  Boolean(account?.organizationId);

const isHelperWorkspace = (workspaceUse) =>
  String(workspaceUse || "").toUpperCase() === "HELPER";

const workspaceUseLabel = (workspaceUse) =>
  isHelperWorkspace(workspaceUse) ? "Helper" : "Requester";

const workspaceRoleIcon = (workspaceUse) =>
  isHelperWorkspace(workspaceUse) ? "construct-outline" : "hand-left-outline";

const WorkspaceModeIcons = ({ account }) => {
  const helper = isHelperWorkspace(account?.workspaceUse);
  const organization = isOrganizationAccount(account);

  return (
    <span className="pm-workspace-mode-icons" aria-hidden="true">
      <OlitaskHeaderIcon name="apps-outline" size={18} />
      <span className="pm-workspace-mode-divider" />
      <span className={organization ? "is-organization" : "is-personal"}>
        <OlitaskHeaderIcon name={organization ? "business-outline" : "person-outline"} size={18} />
      </span>
      <span className="pm-workspace-mode-divider" />
      <span className={helper ? "is-helper" : "is-requester"}>
        <OlitaskHeaderIcon name={workspaceRoleIcon(account?.workspaceUse)} size={18} />
      </span>
    </span>
  );
};

const WorkspacePicker = () => {
  const navigate = useNavigate();
  const {
    workspaces,
    selectedWorkspaceId,
    selectedWorkspace,
    selectWorkspace,
    loading,
  } = useWorkspace();
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);

  const workspaceOptions = workspaces.filter((account) => account?.id);

  const handleWorkspaceSelect = (workspaceId) => {
    if (!workspaceId || workspaceId === selectedWorkspaceId) {
      setOpen(false);
      return;
    }

    selectWorkspace(workspaceId);
    setOpen(false);
    navigate({
      pathname: paths.pmDashboard,
      search: `?wid=${encodeURIComponent(workspaceId)}`,
    });
  };

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!pickerRef.current) return;
      if (!pickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  return (
    <div
      ref={pickerRef}
      className="pm-pa-menu"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Tooltip title="Workspace">
        <button
          type="button"
          className={`pm-pa-trigger${open ? " open" : ""}`}
          onClick={() => setOpen((value) => !value)}
          aria-label="Select workspace"
          aria-expanded={open}
        >
          <WorkspaceModeIcons account={selectedWorkspace} />
        </button>
      </Tooltip>

      {open ? (
        <div className="pm-pa-popdown">
          <div className="pm-pa-popdown-head">
            <div className="pm-pa-popdown-orb">
              <OlitaskHeaderIcon name="checkmark-circle" size={22} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="pm-pa-popdown-kicker">Active Workspace</div>
              <div className="pm-pa-popdown-title">
                {selectedWorkspace
                  ? accountLabel(selectedWorkspace)
                  : "Workspace"}
              </div>
              <div className="pm-pa-popdown-subtitle">
                {selectedWorkspace ? (
                  <>
                    {workspaceUseLabel(selectedWorkspace.workspaceUse)} -{" "}
                    {isOrganizationAccount(selectedWorkspace)
                      ? "Organization workspace"
                      : "Individual workspace"}
                  </>
                ) : (
                  "Choose the workspace this session should use"
                )}
              </div>
            </div>
          </div>
          <div className="pm-pa-popdown-strip">
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              {workspaces.length} available
            </Tag>
            {!isPersonalAccount(selectedWorkspace) && selectedWorkspace?.organizationId ? (
              <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                Business
              </Tag>
            ) : null}
            {selectedWorkspace ? (
              <Tag color={isHelperWorkspace(selectedWorkspace.workspaceUse) ? "green" : "blue"} style={{ marginInlineEnd: 0 }}>
                {workspaceUseLabel(selectedWorkspace.workspaceUse)}
              </Tag>
            ) : null}
          </div>
          <div className="pm-pa-list" aria-label="Available workspaces">
            {loading ? (
              <div className="pm-pa-state">
                <Spin size="small" />
                <span>Loading workspaces</span>
              </div>
            ) : workspaceOptions.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No workspaces"
                style={{ margin: "8px 0" }}
              />
            ) : (
              workspaceOptions.map((account) => {
                const selected = account.id === selectedWorkspaceId;

                return (
                  <button
                    type="button"
                    key={account.id}
                    className={`pm-pa-option${selected ? " is-selected" : ""}`}
                    onClick={() => handleWorkspaceSelect(account.id)}
                    aria-current={selected ? "true" : undefined}
                  >
                    <span className="pm-pa-option-icon">
                      <OlitaskHeaderIcon
                        name={workspaceRoleIcon(account.workspaceUse)}
                        size={20}
                      />
                    </span>
                    <span className="pm-pa-option-copy">
                      <span className="pm-pa-option-name">{accountLabel(account)}</span>
                      <span className="pm-pa-option-meta">
                        {selected ? "Current - " : ""}
                        {workspaceUseLabel(account?.workspaceUse)} -{" "}
                        {isOrganizationAccount(account)
                          ? "Organization workspace"
                          : "Individual workspace"}
                      </span>
                    </span>
                    {selected ? (
                      <OlitaskHeaderIcon className="pm-pa-option-tail is-selected" name="checkmark-circle" size={16} />
                    ) : (
                      <OlitaskHeaderIcon className="pm-pa-option-tail" name="chevron-forward" size={16} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WorkspacePicker;
